import {
	BasicButton,
	CreatePasswordValidation,
	SecondCustomInput,
	Typography,
} from "@/components";
import { BackAppBar } from "@/components/ui";
import useProfile from "@/hooks/useProfile";
import { AuthChangePasswordSchema } from "@/schemas";
import { memberService } from "@/services";
import { zodResolver } from "@hookform/resolvers/zod";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";

export const Route = createFileRoute(
	"/dashboard/accounts/settings/account/new-password",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const [passwordVisible, setPasswordVisible] = useState(false);
	const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
	const {
		control,
		watch,
		handleSubmit,
		formState: { isDirty, isValid, isSubmitting },
	} = useForm<AuthChangePasswordSchema>({
		mode: "all",
		resolver: zodResolver(AuthChangePasswordSchema),
	});

	const { profile } = useProfile();
	const { mutateAsync: mutateAsyncUpdateUser } = useMutation({
		mutationFn: memberService.updateMember.bind(memberService),
	});

	const onSubmit: SubmitHandler<AuthChangePasswordSchema> = (data, e) => {
		e?.stopPropagation();
		e?.preventDefault();
		if (!profile) return;
		return mutateAsyncUpdateUser(
			{
				id: profile.id,
				...data,
			},
			{
				onSuccess: () => {
					toast.success("Password updated");
					return navigate({
						to: "/dashboard/accounts/settings/account",
					});
				},
			},
		);
	};

	return (
		<div className="wrapper !p-0 min-h-full mx-auto">
			<BackAppBar />
			<form onSubmit={handleSubmit(onSubmit)} className="container">
				<Stack spacing={4}>
					<Typography fontWeight={600} fontSize={28}>
						Enter new password
					</Typography>
					<Controller
						control={control}
						name="password"
						defaultValue=""
						render={({ field, fieldState }) => (
							<SecondCustomInput
								type="password"
								label="Password"
								name={field.name}
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
								onToggleVisibility={() => setPasswordVisible(!passwordVisible)}
								showPassword={passwordVisible}
								startIcon={<LockOutlinedIcon />}
								errorMessage={fieldState.error?.message}
							/>
						)}
					/>
					<CreatePasswordValidation value={watch("password")} />
					<Controller
						control={control}
						name="password_confirmation"
						defaultValue=""
						render={({ field, fieldState }) => (
							<SecondCustomInput
								type="password"
								label="Confirm Password"
								name={field.name}
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
								onToggleVisibility={() =>
									setConfirmPasswordVisible(!passwordVisible)
								}
								showPassword={confirmPasswordVisible}
								startIcon={<LockOutlinedIcon />}
								errorMessage={fieldState.error?.message}
							/>
						)}
					/>
				</Stack>
				<Box className="fixed bg-white shadow bottom-0 inset-x-0 z-50 py-3">
					<Box className="wrapper mx-auto">
						<BasicButton
							type="submit"
							variant="contained"
							color="secondary"
							className="w-full !h-12 text-sm font-semibold !shadow-none"
							disabled={!isDirty || !isValid || isSubmitting}
							loading={isSubmitting}
						>
							Update
						</BasicButton>
					</Box>
				</Box>
			</form>
		</div>
	);
}
